import { motion } from "framer-motion";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

const biomarkerTrend = [
  { month: "Jan", hba1c: 7.1, cholesterol: 165 },
  { month: "Mar", hba1c: 6.9, cholesterol: 158 },
  { month: "May", hba1c: 6.7, cholesterol: 152 },
  { month: "Jul", hba1c: 6.5, cholesterol: 148 },
  { month: "Aug", hba1c: 6.4, cholesterol: 142 },
  { month: "Oct", hba1c: 6.2, cholesterol: 128 },
  { month: "Dec", hba1c: 6.1, cholesterol: 118 }
];

const riskRadar = [
  { subject: "Cardiac", score: 72, fullMark: 100 },
  { subject: "Diabetes", score: 58, fullMark: 100 },
  { subject: "Renal", score: 82, fullMark: 100 },
  { subject: "Liver", score: 76, fullMark: 100 },
  { subject: "Mental", score: 64, fullMark: 100 },
  { subject: "Immunity", score: 70, fullMark: 100 }
];

const conditionDistribution = [
  { name: "Cardiovascular", value: 28, color: "#1e3a8a" },
  { name: "Endocrine", value: 22, color: "#1d4ed8" },
  { name: "Musculoskeletal", value: 18, color: "#2563eb" },
  { name: "Respiratory", value: 14, color: "#3b82f6" },
  { name: "Preventive", value: 11, color: "#60a5fa" },
  { name: "Neurological", value: 7, color: "#93c5fd" }
];

const biomarkerRows = [
  { name: "HbA1c", latest: "6.1%", status: "Improving", tone: "ok" },
  { name: "LDL Chol.", latest: "112 mg/dL", status: "Borderline", tone: "warn" },
  { name: "Creatinine", latest: "0.9 mg/dL", status: "Normal", tone: "ok" },
  { name: "TSH", latest: "2.3 mIU/L", status: "Normal", tone: "ok" },
  { name: "Vitamin D", latest: "28 ng/mL", status: "Low", tone: "bad" }
];

const aiAlerts = [
  {
    title: "Vitamin D Deficiency",
    body: "Level at 28 ng/mL — below optimal range. Recommend supplementation review.",
    accent: "border-l-amber-500 bg-amber-50/50"
  },
  {
    title: "HbA1c Improving",
    body: "Consistent decline over 8 months. Maintain current diet and exercise regimen.",
    accent: "border-l-emerald-500 bg-emerald-50/50"
  },
  {
    title: "Annual Cardiac Screening Due",
    body: "Last ECG was 14 months ago. Schedule with your cardiologist.",
    accent: "border-l-blue-500 bg-blue-50/50"
  }
];

const tooltipStyle = {
  contentStyle: {
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 12px rgba(15,23,42,0.06)"
  }
};

/** Recharts needs a non-collapsing box; min-w-0 on grid cells avoids flex/grid blowout. */
const chartBoxClass = "relative h-[260px] w-full min-h-[220px] min-w-[180px] max-w-full sm:h-[280px]";

const cardClass =
  "min-w-0 overflow-hidden rounded-2xl border border-gray-200/90 bg-white p-4 shadow-sm sm:p-5 md:p-6";

function StatusPill({ status, tone }) {
  const styles = {
    ok: "bg-emerald-100 text-emerald-800",
    warn: "bg-amber-100 text-amber-900",
    bad: "bg-red-100 text-red-800"
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[tone]}`}>
      {status}
    </span>
  );
}

export default function LiveDashboardSection() {
  return (
    <section id="live-analytics" className="mx-auto max-w-7xl min-w-0 overflow-x-clip px-6 py-20 md:px-10">
      <div className="mx-auto mb-12 max-w-3xl text-center">
        <span className="inline-block rounded-full bg-amber-100 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-amber-900">
          Live Analytics Preview
        </span>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
          Real-Time Health Intelligence Dashboard
        </h2>
        <p className="mt-4 text-lg text-gray-600">
          Visualize your health data the way clinicians do — with precision, context, and longitudinal depth.
        </p>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={cardClass}
        >
          <h3 className="text-base font-semibold text-gray-900">Lab Biomarker Trends</h3>
          <p className="text-sm text-gray-500">12-month longitudinal view</p>
          <div className={`mt-3 ${chartBoxClass}`}>
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <LineChart data={biomarkerTrend} margin={{ top: 4, right: 6, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="month"
                  label={{ value: "Month", position: "insideBottom", offset: -4, fill: "#6b7280", fontSize: 11 }}
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  domain={[5.5, 7.5]}
                  label={{ value: "HbA1c (%)", angle: -90, position: "insideLeft", fill: "#1d4ed8", fontSize: 11 }}
                  tick={{ fill: "#1d4ed8", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[100, 190]}
                  label={{
                    value: "Cholesterol (mg/dL)",
                    angle: 90,
                    position: "insideRight",
                    fill: "#2563eb",
                    fontSize: 11
                  }}
                  tick={{ fill: "#2563eb", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip {...tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="hba1c"
                  name="HbA1c (%)"
                  stroke="#1d4ed8"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cholesterol"
                  name="Cholesterol"
                  stroke="#60a5fa"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05 }}
          className={cardClass}
        >
          <h3 className="text-base font-semibold text-gray-900">Health Risk Scores</h3>
          <p className="text-sm text-gray-500">Multi-domain risk profile</p>
          <div className={`mt-3 ${chartBoxClass}`}>
            <ResponsiveContainer width="100%" height="100%" minHeight={220}>
              <RadarChart data={riskRadar} margin={{ top: 12, right: 12, bottom: 8, left: 12 }}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "#4b5563", fontSize: 10 }} />
                {/* Ticks off: avoids Recharts layout bugs when the card is narrow (e.g. xl 4-col row). */}
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                <Radar
                  name="Your Profile"
                  dataKey="score"
                  stroke="#1d4ed8"
                  fill="#3b82f6"
                  fillOpacity={0.35}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
                <Tooltip {...tooltipStyle} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.08 }}
          className={cardClass}
        >
          <h3 className="text-base font-semibold text-gray-900">Condition Category Distribution</h3>
          <p className="text-sm text-gray-500">Across all uploaded records</p>
          <div className="mt-3 flex min-h-[240px] flex-col gap-3 xl:min-h-[260px]">
            <div className="mx-auto aspect-square h-[min(200px,42vw)] w-[min(200px,42vw)] max-h-[200px] max-w-[200px] shrink-0 sm:mx-0 xl:mx-auto">
              <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                <PieChart>
                  <Pie
                    data={conditionDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="52%"
                    outerRadius="78%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {conditionDistribution.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="min-w-0 space-y-1.5 text-xs leading-snug text-gray-700 sm:text-sm">
              {conditionDistribution.map((row) => (
                <li key={row.name} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: row.color }} />
                    <span className="truncate">{row.name}</span>
                  </span>
                  <span className="shrink-0 font-medium tabular-nums text-gray-900">{row.value.toFixed(1)}%</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className={cardClass}
        >
          <h3 className="text-base font-semibold text-gray-900">Recent Biomarker Status</h3>
          <div className="mt-3 w-full min-w-0">
            <table className="w-full table-fixed border-collapse text-left text-xs sm:text-sm">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[38%]" />
                <col className="w-[30%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-200 text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-xs">
                  <th className="pb-2 pr-1 font-medium">Biomarker</th>
                  <th className="pb-2 pr-1 font-medium">Latest</th>
                  <th className="pb-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {biomarkerRows.map((row) => (
                  <tr key={row.name}>
                    <td className="py-2 pr-1 font-medium text-gray-900">
                      <span className="line-clamp-2 break-words">{row.name}</span>
                    </td>
                    <td className="py-2 pr-1 break-words text-gray-600">{row.latest}</td>
                    <td className="py-2 align-middle">
                      <StatusPill status={row.status} tone={row.tone} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className={`${cardClass} mt-5`}
      >
        <h3 className="text-base font-semibold text-gray-900">AI Alerts &amp; Recommendations</h3>
        <ul className="mt-4 space-y-3">
          {aiAlerts.map((alert) => (
            <li
              key={alert.title}
              className={`rounded-xl border border-l-4 border-gray-100 ${alert.accent} px-4 py-3`}
            >
              <p className="font-semibold text-gray-900">{alert.title}</p>
              <p className="mt-1 text-sm text-gray-600">{alert.body}</p>
            </li>
          ))}
        </ul>
      </motion.div>
    </section>
  );
}
